import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Upload, 
  FileText, 
  Download, 
  Trash2, 
  File,
  FileImage,
  FileSpreadsheet,
  Loader2,
  Eye,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/errorHandler';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const DocumentManager = ({ entityType, entityId, user }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('invoice');
  const [customDocumentType, setCustomDocumentType] = useState('');
  const [notes, setNotes] = useState('');

  const documentTypeOrder = [
    'shipping_documents',
    'bl_surrendered',
    'invoice',
    'inspection_certificate',
    'amended_bl_copy',
    'bl_copy',
    'proforma_invoice',
    'pro_forma_invoice',
    'proforma',
    'export_certificate_japanese',
    'export_certificate_english'
  ];

  const normalizeDocType = (type) => {
    if (!type) return '';
    return type.toLowerCase().trim().replace(/[\s-]+/g, '_');
  };

  const getDocTypeRank = (docType) => {
    const normalized = normalizeDocType(docType);
    const index = documentTypeOrder.indexOf(normalized);
    return index === -1 ? documentTypeOrder.length + 10 : index;
  };

  const sortedDocuments = [...documents].sort((a, b) => {
    if (a.sort_order != null || b.sort_order != null) {
      const aOrder = a.sort_order ?? 0;
      const bOrder = b.sort_order ?? 0;
      if (aOrder !== bOrder) return aOrder - bOrder;
    }
    const rankDiff = getDocTypeRank(a.document_type) - getDocTypeRank(b.document_type);
    if (rankDiff !== 0) return rankDiff;
    return new Date(a.uploaded_at) - new Date(b.uploaded_at);
  });

  const hasPermission = (action) => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'employee' && user?.permissions) {
      return user.permissions[action] === true;
    }
    return false;
  };

  useEffect(() => {
    if (entityId) {
      fetchDocuments();
    }
  }, [entityId]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents/${entityType}/${entityId}`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    if (documentType === 'other' && !customDocumentType.trim()) {
      toast.error('Please enter a document name for "Other" type');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const finalDocumentType = documentType === 'other' ? customDocumentType.trim() : documentType;
      
      const params = new URLSearchParams({
        entity_type: entityType,
        entity_id: entityId,
        document_type: finalDocumentType
      });
      
      if (notes) {
        params.append('notes', notes);
      }

      await axios.post(`${API}/documents/upload?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document uploaded successfully!');
      setSelectedFile(null);
      setNotes('');
      setDocumentType('invoice');
      setCustomDocumentType('');
      fetchDocuments();
      
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error uploading document:', error);
      const message = extractErrorMessage(error, 'Failed to upload document');
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setDeleting(documentId);
    try {
      await axios.delete(`${API}/documents/${documentId}`);
      toast.success('Document deleted successfully!');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete document';
      toast.error(errorMessage);
    } finally {
      setDeleting(null);
    }
  };

  const handleDownload = async (document) => {
    try {
      const response = await axios.get(`${API}/documents/${document.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.original_filename || document.filename);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Document downloaded successfully!');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleView = async (document) => {
    try {
      const token = localStorage.getItem('token');
      const viewUrl = `${API}/document-preview/${document.id}`;
      
      const response = await axios.get(viewUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: document.file_type });
      const url = window.URL.createObjectURL(blob);
      
      window.open(url, '_blank');
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Failed to view document. Please try downloading instead.');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reordered = Array.from(sortedDocuments);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    setDocuments(reordered);

    try {
      await axios.post(`${API}/documents/reorder`, {
        entity_type: entityType,
        entity_id: entityId,
        ordered_ids: reordered.map(doc => doc.id)
      });
      toast.success('Document order updated');
    } catch (error) {
      console.error('Error reordering documents:', error);
      toast.error('Failed to reorder documents');
      fetchDocuments();
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (fileType?.includes('image')) return <FileImage className="h-5 w-5 text-blue-500" />;
    if (fileType?.includes('document') || fileType?.includes('word')) 
      return <FileSpreadsheet className="h-5 w-5 text-blue-600" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderDocumentList = (isDraggable = false) => {
    if (loading) {
      return (
        <div className="text-center py-4">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
        </div>
      );
    }

    if (documents.length === 0) {
      return (
        <div className="text-center py-8 text-slate-500 border rounded-lg">
          <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">No documents uploaded yet</p>
          <p className="text-xs">Upload invoices, receipts, or other documents</p>
        </div>
      );
    }

    if (isDraggable) {
      return (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="documents">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                {sortedDocuments.map((doc, index) => (
                  <Draggable key={doc.id} draggableId={doc.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 ${
                          snapshot.isDragging ? 'bg-slate-50 shadow-sm' : ''
                        }`}
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div {...provided.dragHandleProps} className="text-slate-400">
                            <GripVertical className="h-4 w-4" />
                          </div>
                          {getFileIcon(doc.file_type)}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                            <div className="flex items-center space-x-2 text-xs text-slate-500">
                              <span className="capitalize">{(doc.document_type || '').replace('_', ' ')}</span>
                              <span>|</span>
                              <span>{formatFileSize(doc.file_size)}</span>
                              <span>|</span>
                              <span>{formatDate(doc.uploaded_at)}</span>
                            </div>
                            {doc.notes && (
                              <p className="text-xs text-slate-500 mt-1 italic">{doc.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(doc)}
                            title="View document"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(doc)}
                            title="Download document"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {hasPermission('documents_edit') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(doc.id)}
                              disabled={deleting === doc.id}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete document"
                            >
                              {deleting === doc.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      );
    }

    return (
      <div className="space-y-2">
        {sortedDocuments.map((doc) => (
          <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {getFileIcon(doc.file_type)}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                <div className="flex items-center space-x-2 text-xs text-slate-500">
                  <span className="capitalize">{(doc.document_type || '').replace('_', ' ')}</span>
                  <span>|</span>
                  <span>{formatFileSize(doc.file_size)}</span>
                  <span>|</span>
                  <span>{formatDate(doc.uploaded_at)}</span>
                </div>
                {doc.notes && (
                  <p className="text-xs text-slate-500 mt-1 italic">{doc.notes}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleView(doc)}
                title="View document"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(doc)}
                title="Download document"
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCustomerView = () => {
    if (loading) {
      return (
        <div className="text-center py-4">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
        </div>
      );
    }

    if (documents.length === 0) {
      return (
        <div className="text-center py-8 text-slate-500">
          <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" />
          <p>No documents available</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {sortedDocuments.map((doc) => (
          <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
            <div className="flex items-center space-x-3 flex-1">
              {getFileIcon(doc.file_type)}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                <p className="text-xs text-slate-500">
                  {formatFileSize(doc.file_size)} | {formatDate(doc.uploaded_at)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleView(doc)}
                title="View document"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(doc)}
                title="Download document"
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (user?.role === 'customer') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Documents</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderCustomerView()}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <FileText className="h-5 w-5" />
          <span>Document Management</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasPermission('documents_edit') && (
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file-upload">Upload Document</Label>
              <Input
                id="file-upload"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              {selectedFile && (
                <p className="text-sm text-slate-600">
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="document-type">Document Type</Label>
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shipping_documents">ALL DOCUMENTS</SelectItem>
                    <SelectItem value="invoice">INVOICE</SelectItem>
                    <SelectItem value="export_certificate_japanese">EXPORT CERTIFICATE (JAPANESE)</SelectItem>
                    <SelectItem value="export_certificate_english">EXPORT CERTIFICATE (ENGLISH)</SelectItem>
                    <SelectItem value="inspection_certificate">INSPECTION CERTIFICATE</SelectItem>
                    <SelectItem value="bl_copy">BL (COPY)</SelectItem>
                    <SelectItem value="amended_bl_copy">AMENDED BL COPY</SelectItem>
                    <SelectItem value="bl_surrendered">BL (SURRENDERED)</SelectItem>
                    <SelectItem value="proforma_invoice">PRO-FORMA INVOICE</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {documentType === 'other' ? (
                <div>
                  <Label htmlFor="custom-document-type">Document Name *</Label>
                  <Input
                    id="custom-document-type"
                    value={customDocumentType}
                    onChange={(e) => setCustomDocumentType(e.target.value)}
                    placeholder="Enter document name..."
                    disabled={uploading}
                    required
                  />
                </div>
              ) : (
                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Input
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes..."
                    disabled={uploading}
                  />
                </div>
              )}
            </div>
            
            {documentType === 'other' && (
              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Input
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes..."
                  disabled={uploading}
                />
              </div>
            )}

            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="w-full"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </>
              )}
            </Button>
          </div>
        )}

        <div>
          <h4 className="font-medium text-sm mb-3">Uploaded Documents ({documents.length})</h4>
          <div className="max-h-64 overflow-y-auto">
            {renderDocumentList(hasPermission('documents_edit'))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentManager;